import type { IntegrationPlatform, Licence, LicenceInput, LicenceIntegration } from '../api/admin/types';
import { INTEGRATION_PLATFORMS } from '../api/shared';

import {
  endOfDayIso,
  parseOptionalWholeNumber,
  startOfDayIso,
  toDateInputValue,
  todayInputValue,
} from './parse';

/** A licence as the form edits it: text, because an empty limit field means "unlimited" (BR-07). */
export type LicenceDraft = {
  validFrom: string;
  validUntil: string;
  maxCompanies: string;
  maxSubUsers: string;
  maxProducts: string;
  maxTotalStock: string;
  /** How many shops of each platform may be linked; empty is none — not unlimited (R0009, R0012). */
  integrationLinks: Record<IntegrationPlatform, string>;
  notes: string;
};

export type LimitField = 'maxCompanies' | 'maxSubUsers' | 'maxProducts' | 'maxTotalStock';

export const LIMIT_FIELDS: Array<LimitField> = [
  'maxCompanies',
  'maxSubUsers',
  'maxProducts',
  'maxTotalStock',
];

const limitText = (value: number | null): string => (value === null ? '' : String(value));

export const emptyLicenceDraft = (): LicenceDraft => ({
  validFrom: todayInputValue(),
  validUntil: '',
  maxCompanies: '',
  maxSubUsers: '',
  maxProducts: '',
  maxTotalStock: '',
  integrationLinks: noLinks(),
  notes: '',
});

const noLinks = (): Record<IntegrationPlatform, string> =>
  Object.fromEntries(INTEGRATION_PLATFORMS.map((platform) => [platform, ''])) as Record<
    IntegrationPlatform,
    string
  >;

/** The licensed integrations from the fields: a platform left empty or 0 is not licensed; `undefined` when a field is not a whole number. */
export const integrationsFrom = (
  links: Record<IntegrationPlatform, string>,
): Array<LicenceIntegration> | undefined => {
  const items: Array<LicenceIntegration> = [];

  for (const platform of INTEGRATION_PLATFORMS) {
    const maxLinks = parseOptionalWholeNumber(links[platform] ?? '');

    if (maxLinks === undefined) {
      return undefined;
    }

    if (maxLinks !== null && maxLinks > 0) {
      items.push({ platform, maxLinks });
    }
  }

  return items;
};

export const licenceDraftFrom = (licence: Licence): LicenceDraft => ({
  validFrom: toDateInputValue(licence.validFrom),
  validUntil: toDateInputValue(licence.validUntil),
  maxCompanies: limitText(licence.limits.companies),
  maxSubUsers: limitText(licence.limits.subUsers),
  maxProducts: limitText(licence.limits.products),
  maxTotalStock: limitText(licence.limits.totalStock),
  integrationLinks: Object.fromEntries(
    INTEGRATION_PLATFORMS.map((platform) => [
      platform,
      String((licence.integrations ?? []).find((item) => item.platform === platform)?.maxLinks ?? ''),
    ]),
  ) as Record<IntegrationPlatform, string>,
  notes: licence.notes ?? '',
});

/** The input the API takes, or `null` when a field cannot be read — the form then says which. */
export const licenceInputFrom = (draft: LicenceDraft): LicenceInput | null => {
  const limits = LIMIT_FIELDS.map((field) => parseOptionalWholeNumber(draft[field]));

  const integrations = integrationsFrom(draft.integrationLinks);

  if (draft.validFrom === '' || limits.some((limit) => limit === undefined) || integrations === undefined) {
    return null;
  }

  if (draft.validUntil !== '' && draft.validUntil < draft.validFrom) {
    return null;
  }

  const [maxCompanies, maxSubUsers, maxProducts, maxTotalStock] = limits as Array<number | null>;

  return {
    validFrom: startOfDayIso(draft.validFrom),
    validUntil: draft.validUntil === '' ? null : endOfDayIso(draft.validUntil),
    maxCompanies,
    maxSubUsers,
    maxProducts,
    maxTotalStock,
    notes: draft.notes.trim() === '' ? null : draft.notes.trim(),
    integrations,
  };
};
